import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Combobox,
  EmptyState,
  IconButton,
  Input,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useAuth } from '../../auth/AuthContext';
import { useT } from '../../i18n';
import { approvalsApi } from '../approvals/approvalsApi';

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

/**
 * Out-of-office cover: lets the signed-in user hand their approval authority to a colleague for a
 * date range. While active, the delegate can approve/reject the documents the delegator's roles
 * cover. Mirrors the self-service style of the notification-preferences card.
 */
export default function DelegationCard() {
  const toast = useToast();
  const { user } = useAuth();
  const t = useT();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [candidates, setCandidates] = useState([]);

  const [toUserId, setToUserId] = useState('');
  const [startsAt, setStartsAt] = useState(todayIso());
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mine, cands] = await Promise.all([
        approvalsApi.listMyDelegations(),
        approvalsApi.delegationCandidates(),
      ]);
      setRows(mine || []);
      setCandidates(cands || []);
    } catch (err) {
      toast.error(t('Could not load delegations'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => { load(); }, [load]);

  const candidateOptions = useMemo(
    () => candidates.map((c) => ({ value: c.id, label: c.name, description: c.email })),
    [candidates],
  );

  const create = async () => {
    if (!toUserId) { toast.error(t('Choose a colleague to cover for you.')); return; }
    if (!startsAt || !endsAt) { toast.error(t('Pick a start and end date.')); return; }
    if (endsAt < startsAt) { toast.error(t('The end date must be on or after the start date.')); return; }
    setSaving(true);
    try {
      // End-of-day on the end date so the final day is fully covered.
      await approvalsApi.createDelegation({
        toUserId,
        startsAt: new Date(`${startsAt}T00:00:00`).toISOString(),
        endsAt: new Date(`${endsAt}T23:59:59`).toISOString(),
        reason: reason.trim() || null,
        scopePropertyId: null,
      });
      toast.success(t('Delegation scheduled'));
      setToUserId('');
      setReason('');
      setEndsAt('');
      await load();
    } catch (err) {
      toast.error(t('Could not create delegation'), { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (id) => {
    try {
      await approvalsApi.cancelDelegation(id);
      toast.success(t('Delegation cancelled'));
      await load();
    } catch (err) {
      toast.error(t('Could not cancel delegation'), { description: err?.message });
    }
  };

  const myId = user?.id;
  const outgoing = rows.filter((r) => r.fromUserId === myId);
  const incoming = rows.filter((r) => r.toUserId === myId);

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t('Out-of-office cover')}</Card.Title>
        <Card.Subtitle>{t("Delegate your approvals to a colleague while you're away.")}</Card.Subtitle>
      </Card.Header>
      <Card.Body>
        <div className="acct-deleg">
          <div className="acct-deleg__form">
            <div className="ps-field">
              <span className="ps-field__label">{t('Delegate to')}</span>
              <Combobox
                options={candidateOptions}
                value={toUserId}
                onChange={setToUserId}
                placeholder={t('Choose a colleague…')}
                searchPlaceholder={t('Search colleagues…')}
                emptyText={t('No colleagues found')}
                ariaLabel={t('Delegate approvals to')}
              />
            </div>
            <div className="acct-form__row">
              <Input type="date" label={t('From')} value={startsAt} min={todayIso()} onChange={(e) => setStartsAt(e.target.value)} />
              <Input type="date" label={t('Until')} value={endsAt} min={startsAt || todayIso()} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
            <Textarea
              label={t('Reason (optional)')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('e.g. Annual leave')}
              rows={2}
            />
            <div className="acct-form__actions">
              <Button variant="primary" loading={saving} onClick={create}>{t('Schedule cover')}</Button>
            </div>
          </div>

          <div className="acct-deleg__list">
            {loading ? (
              <div className="acct-deleg__loading"><Spinner /></div>
            ) : (outgoing.length === 0 && incoming.length === 0) ? (
              <EmptyState
                icon={<Icon.Users size={24} />}
                title={t('No active cover')}
                description={t("Schedule a colleague to approve in your place while you're out.")}
              />
            ) : (
              <>
                {outgoing.length > 0 && (
                  <div className="acct-deleg__group">
                    <span className="acct-deleg__group-label">{t('You delegated')}</span>
                    {outgoing.map((d) => (
                      <DelegationRow key={d.id} d={d} who={t('to {name}', { name: d.toUserName || t('colleague') })} onCancel={() => cancel(d.id)} />
                    ))}
                  </div>
                )}
                {incoming.length > 0 && (
                  <div className="acct-deleg__group">
                    <span className="acct-deleg__group-label">{t('Covering for')}</span>
                    {incoming.map((d) => (
                      <DelegationRow key={d.id} d={d} who={t('from {name}', { name: d.fromUserName || t('colleague') })} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

function DelegationRow({ d, who, onCancel }) {
  const t = useT();
  return (
    <div className="acct-deleg__row">
      <div className="acct-deleg__row-main">
        <span className="acct-deleg__row-who">{who}</span>
        <span className="acct-form__muted">{fmtDate(d.startsAt)} – {fmtDate(d.endsAt)}</span>
        {d.reason && <span className="acct-deleg__row-reason">{d.reason}</span>}
      </div>
      <div className="acct-deleg__row-side">
        <Badge tone={d.isActive ? 'success' : 'default'} dot>{d.isActive ? t('Active') : t('Scheduled')}</Badge>
        {onCancel && (
          <IconButton size="sm" variant="ghost" aria-label={t('Cancel delegation')} onClick={onCancel}>
            <Icon.Trash size={15} />
          </IconButton>
        )}
      </div>
    </div>
  );
}
